import axios from 'axios';
import { SchedulingJobControllerApi } from '../api/generated';
import type { SchedulingJobResultResponse } from '../api/generated';
import { createApiConfiguration } from '../api/apiConfig';

export interface WeekResultRequest {
  locationId: string;
  scenarioId: string;
  weekNumber: number;
}

// Three outcomes rather than a result-or-throw, because the difference that
// matters is not success versus failure: it is "this week has no plan yet"
// versus "we could not find out". Callers that collapse the two end up telling
// a planner a week is empty when the request actually failed.
export type WeekResultOutcome =
  | { kind: 'planned'; response: SchedulingJobResultResponse }
  | { kind: 'unplanned' }
  | { kind: 'failed'; status: number | undefined };

// Fetches the newest completed solver result for one week and classifies the
// answer. Never rejects — every caller has to handle all three outcomes — and
// logs each failure, so the decision about what to show the user stays separate
// from the record of what went wrong.
export const fetchWeekSchedulingResult = async (
  request: WeekResultRequest
): Promise<WeekResultOutcome> => {
  const context = `week ${request.weekNumber}, scenario ${request.scenarioId}`;

  try {
    const { data } = await new SchedulingJobControllerApi(
      createApiConfiguration()
    ).getCurrentResult(request);

    // A 200 with an unreadable body (an empty string from a proxy, a JSON `null`
    // for a pruned result) must not pass for a plan: the mappers dereference
    // `response.result` and would take the whole app down with it.
    //
    // `typeof null === 'object'`, and an array is an object too, so the type
    // check alone let both through. The payload has to actually carry a
    // `result` before it can be called a plan.
    if (data === null || typeof data !== 'object' || !('result' in data)) {
      console.error(`Unreadable week scheduling result (${context}):`, data);
      return { kind: 'failed', status: 200 };
    }
    return { kind: 'planned', response: data };
  } catch (error: unknown) {
    const status = axios.isAxiosError(error) ? error.response?.status : undefined;

    if (status === 404) {
      // The backend answers 404 for "no completed calculation yet", but also for
      // a scenario it cannot find — and a misrouted call or a wrong API base URL
      // is indistinguishable from both. So the unplanned reading is recorded
      // rather than trusted silently: a wall of these means the scenario or the
      // URL is wrong, not that the year is unplanned. Narrowing this properly
      // needs the backend to answer 204 for "no result yet" and keep 404 for a
      // missing scenario.
      console.debug(`No completed calculation for ${context}`);
      return { kind: 'unplanned' };
    }
    console.error(
      `Failed to load week scheduling result (${context}, status ${status ?? 'n/a'}):`,
      error
    );
    return { kind: 'failed', status };
  }
};
